import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressPortfolioCollectionsCollectionsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/collections-service',
        destPath: '/api',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/collections-service',
        destPath: '/api',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/collections-service',
        destPath: '/api',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/collections-service',
        destPath: '/api',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/collections-service',
        destPath: '/api',
      },
    ],
    _: [
      {
        srcPath: '/_api/collections-service',
        destPath: '/api',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/collections-service',
        destPath: '/api',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/portfolio/v1/collections',
        destPath: '/api/v1/portfolio/collections',
      },
      {
        srcPath: '/portfolio/collections/api/v1/portfolio/collections',
        destPath: '/api/v1/portfolio/collections',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/collections-service',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_portfolio_collections';

/** Creates a collection. */
export function createCollection(payload: object): RequestOptionsFactory<any> {
  function __createCollection({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'collection.sortOrder' },
          { path: 'collection.coverImage.focalPoint.x' },
          { path: 'collection.coverImage.focalPoint.y' },
          { path: 'collection.coverImage.unsharpMasking.amount' },
          { path: 'collection.coverImage.unsharpMasking.radius' },
          { path: 'collection.coverImage.unsharpMasking.threshold' },
        ],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'collection.createdDate' },
          { path: 'collection.updatedDate' },
          { path: 'collection.coverImage.imageInfo.urlExpirationDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.portfolio.collections.v1.collection',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.portfolio.collections.CollectionsService.CreateCollection',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPortfolioCollectionsCollectionsServiceUrl({
        protoPath: '/api/v1/portfolio/collections',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'collection.sortOrder' },
              { path: 'collection.coverImage.focalPoint.x' },
              { path: 'collection.coverImage.focalPoint.y' },
              { path: 'collection.coverImage.unsharpMasking.amount' },
              { path: 'collection.coverImage.unsharpMasking.radius' },
              { path: 'collection.coverImage.unsharpMasking.threshold' },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'collection.createdDate' },
              { path: 'collection.updatedDate' },
              { path: 'collection.coverImage.imageInfo.urlExpirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createCollection;
}

/** Retrieves a collection. */
export function getCollection(payload: object): RequestOptionsFactory<any> {
  function __getCollection({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.portfolio.collections.v1.collection',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.portfolio.collections.CollectionsService.GetCollection',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPortfolioCollectionsCollectionsServiceUrl({
        protoPath: '/api/v1/portfolio/collections/{collectionId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'collection.sortOrder' },
              { path: 'collection.coverImage.focalPoint.x' },
              { path: 'collection.coverImage.focalPoint.y' },
              { path: 'collection.coverImage.unsharpMasking.amount' },
              { path: 'collection.coverImage.unsharpMasking.radius' },
              { path: 'collection.coverImage.unsharpMasking.threshold' },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'collection.createdDate' },
              { path: 'collection.updatedDate' },
              { path: 'collection.coverImage.imageInfo.urlExpirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getCollection;
}

/** Retrieves a list all collections in a portfolio. */
export function listCollections(payload: object): RequestOptionsFactory<any> {
  function __listCollections({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.portfolio.collections.v1.collection',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.portfolio.collections.CollectionsService.ListCollections',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPortfolioCollectionsCollectionsServiceUrl({
        protoPath: '/api/v1/portfolio/collections',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'collections.sortOrder' },
              { path: 'collections.coverImage.focalPoint.x' },
              { path: 'collections.coverImage.focalPoint.y' },
              { path: 'collections.coverImage.unsharpMasking.amount' },
              { path: 'collections.coverImage.unsharpMasking.radius' },
              { path: 'collections.coverImage.unsharpMasking.threshold' },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'collections.createdDate' },
              { path: 'collections.updatedDate' },
              { path: 'collections.coverImage.imageInfo.urlExpirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __listCollections;
}

/** Updates a collection. */
export function updateCollection(payload: object): RequestOptionsFactory<any> {
  function __updateCollection({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'mask' }],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'collection.sortOrder' },
          { path: 'collection.coverImage.focalPoint.x' },
          { path: 'collection.coverImage.focalPoint.y' },
          { path: 'collection.coverImage.unsharpMasking.amount' },
          { path: 'collection.coverImage.unsharpMasking.radius' },
          { path: 'collection.coverImage.unsharpMasking.threshold' },
        ],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'collection.createdDate' },
          { path: 'collection.updatedDate' },
          { path: 'collection.coverImage.imageInfo.urlExpirationDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.portfolio.collections.v1.collection',
      method: 'PATCH' as any,
      methodFqn:
        'com.wixpress.portfolio.collections.CollectionsService.UpdateCollection',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPortfolioCollectionsCollectionsServiceUrl({
        protoPath: '/api/v1/portfolio/collections/{collection.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'collection.sortOrder' },
              { path: 'collection.coverImage.focalPoint.x' },
              { path: 'collection.coverImage.focalPoint.y' },
              { path: 'collection.coverImage.unsharpMasking.amount' },
              { path: 'collection.coverImage.unsharpMasking.radius' },
              { path: 'collection.coverImage.unsharpMasking.threshold' },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'collection.createdDate' },
              { path: 'collection.updatedDate' },
              { path: 'collection.coverImage.imageInfo.urlExpirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateCollection;
}

/** Deletes a collection. */
export function deleteCollection(payload: object): RequestOptionsFactory<any> {
  function __deleteCollection({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.portfolio.collections.v1.collection',
      method: 'DELETE' as any,
      methodFqn:
        'com.wixpress.portfolio.collections.CollectionsService.DeleteCollection',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPortfolioCollectionsCollectionsServiceUrl({
        protoPath: '/api/v1/portfolio/collections/{collectionId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteCollection;
}

/**
 * Creates a query to retrieve a list of collections.
 *
 * The `queryCollections()` method builds a query to retrieve a list of collections and returns a `CollectionsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is used to run the query using the [`find()`](https://dev.wix.com/docs/sdk/backend-modules/portfolio/collections/collections-query-builder/find) method.
 *
 * You can refine the query by chaining `CollectionsQueryBuilder` methods onto the query. `CollectionsQueryBuilder` methods enable you to filter, sort, and control the results that `queryCollections()` returns.
 *
 * `queryCollections()`  runs with the following `CollectionsQueryBuilder` defaults which you can override:
 *
 * + [`limit(100)`](https://dev.wix.com/docs/sdk/backend-modules/portfolio/collections/collections-query-builder/limit)
 * + [`ascending('_id')`](https://dev.wix.com/docs/sdk/backend-modules/portfolio/collections/collections-query-builder/ascending)
 *
 * The following `CollectionsQueryBuilder` methods are supported for `queryCollections()`. For a full description of the operations object, see the object returned for the [`items`](https://dev.wix.com/docs/sdk/backend-modules/portfolio/collections/collections-query-result/items) property in `CollectionsQueryResult`.
 */
export function queryCollections(payload: object): RequestOptionsFactory<any> {
  function __queryCollections({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.portfolio.collections.v1.collection',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.portfolio.collections.CollectionsService.QueryCollections',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressPortfolioCollectionsCollectionsServiceUrl({
        protoPath: '/api/v1/portfolio/collections/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              { path: 'collections.sortOrder' },
              { path: 'collections.coverImage.focalPoint.x' },
              { path: 'collections.coverImage.focalPoint.y' },
              { path: 'collections.coverImage.unsharpMasking.amount' },
              { path: 'collections.coverImage.unsharpMasking.radius' },
              { path: 'collections.coverImage.unsharpMasking.threshold' },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'collections.createdDate' },
              { path: 'collections.updatedDate' },
              { path: 'collections.coverImage.imageInfo.urlExpirationDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryCollections;
}
